import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSkeletonState } from "../hooks/useSkeletonState.ts";
import { useLoadingIndicator } from "../hooks/useLoadingIndicator.tsx";
import { usePrintDrain } from "../print/usePrintDrain.ts";
import { useInfiniteScroll } from "./useInfiniteScroll.ts";
import { useLaggedFlag } from "./useLaggedFlag.ts";
import { useLoadMoreAnnouncement } from "./useLoadMoreAnnouncement.ts";
import { useSettledCountAnnouncement } from "./useSettledCountAnnouncement.ts";

export interface CollectionPage<TItem> {
    items: TItem[];
    totalCount: number;
    continuationToken?: string;
}

export type CollectionErrorPosture = "throw" | "inline";

export interface UseInfiniteCollectionOptions<TItem, TPage extends CollectionPage<TItem> = CollectionPage<TItem>> {
    queryKey: QueryKey;
    queryFn: (continuationToken: string | undefined) => Promise<TPage>;
    placeholder: TItem[];
    hasActiveFilters: boolean;
    getLoadedMoreMessage: (count: number) => string;
    getRefreshedMessage?: (count: number) => string;
    onError?: CollectionErrorPosture;
    isCompanionPending?: boolean;
    retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
}

export interface UseInfiniteCollectionResult<TItem, TPage extends CollectionPage<TItem> = CollectionPage<TItem>> {
    items: TItem[];
    totalCount: number | undefined;
    firstPage: TPage | undefined;
    isLoading: boolean;
    isRefreshing: boolean;
    isEmpty: boolean;
    hasContent: boolean;
    isColdError: boolean;
    isStaleError: boolean;
    hasActiveFilters: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    setSentinelRef: (element: HTMLDivElement | null) => void;
    loadMore: () => void;
    refetch: () => void;
    error: Error | null;
    loadedAnnouncement: string;
    refreshedAnnouncement: string;
}

export function useInfiniteCollection<TItem, TPage extends CollectionPage<TItem> = CollectionPage<TItem>>({
    queryKey,
    queryFn,
    placeholder,
    hasActiveFilters,
    getLoadedMoreMessage,
    getRefreshedMessage,
    onError = "throw",
    isCompanionPending = false,
    retry
}: UseInfiniteCollectionOptions<TItem, TPage>): UseInfiniteCollectionResult<TItem, TPage> {
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isPending,
        isError: isQueryError,
        isPlaceholderData,
        error,
        refetch
    } = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }) => queryFn(pageParam),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: lastPage => lastPage.continuationToken,
        placeholderData: keepPreviousData,
        throwOnError: onError === "throw",
        ...(retry !== undefined && { retry })
    });

    const allItems = useMemo(() => data?.pages.flatMap(page => page.items), [data?.pages]);

    const firstPage = data?.pages[0];

    const totalCount = firstPage?.totalCount;

    const { items, hasContent, isEmpty, isLoading } = useSkeletonState({
        isPending: isPending || isCompanionPending,
        data: allItems,
        placeholder
    });

    const hasKeptPage = allItems !== undefined;
    const isColdError = isQueryError && !hasKeptPage;
    const isStaleError = isQueryError && hasKeptPage;

    const laggedHasActiveFilters = useLaggedFlag(hasActiveFilters, isPlaceholderData);

    const loadMore = useCallback(() => {
        void fetchNextPage();
    }, [fetchNextPage]);

    const setSentinelRef = useInfiniteScroll({
        hasNextPage,
        isFetchingNextPage,
        isPaused: isPlaceholderData,
        onLoadMore: loadMore
    });

    const refetchCollection = useCallback(() => {
        void refetch();
    }, [refetch]);

    const loadedAnnouncement = useLoadMoreAnnouncement({
        isFetchingNextPage,
        lastPageItemCount: data?.pages.at(-1)?.items.length,
        getMessage: getLoadedMoreMessage
    });

    const isRefreshingRaw = isPlaceholderData && !isLoading;

    const isRefreshing = useLoadingIndicator(isRefreshingRaw, { minDurationMs: 0 });

    usePrintDrain({
        hasNextPage,
        isFetchingNextPage,
        isPending,
        isPlaceholderData,
        isSettling: isLoading || isRefreshing,
        loadMore
    });

    const refreshedAnnouncement = useSettledCountAnnouncement({
        isRefreshing,
        totalCount: getRefreshedMessage ? totalCount : undefined,
        getMessage: getRefreshedMessage ?? (() => "")
    });

    return {
        items,
        totalCount,
        firstPage,
        isLoading,
        isRefreshing,
        isEmpty,
        hasContent,
        isColdError,
        isStaleError,
        hasActiveFilters: laggedHasActiveFilters,
        hasNextPage,
        isFetchingNextPage,
        setSentinelRef,
        loadMore,
        refetch: refetchCollection,
        error: isColdError ? error : null,
        loadedAnnouncement,
        refreshedAnnouncement
    };
}
